import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { Text, ServiceIcons, SkeletonText } from "@zapier/design-system";
import { Tooltip } from "@zapier/design-system-beta";
import {
  agentAtomFamily_SUSPENDS,
  AgentFetchResult,
  hasStreamingProgressStartedAtom,
} from "@zapier/canvas-state";
import { getUniqueAgentWorkflowApps } from "../../../../../../utils/getUniqueAgentWorkflowApps";
import useServiceById from "../../../../../../hooks/useServiceById";
import cleanUpAppId from "../../../../../../utils/cleanUpAppId";
import getAppIconUrl from "../../../../../../utils/getAppIconUrl";
import styles from "./AgentThumbnail.module.css";

type Props = {
  agentId: string;
};

/** Special agent apps that implementations API don't have data for
 * Copied from https://gitlab.com/zapier/zapai/ai-command-center/-/blob/staging/apps/mcp/src/components/workflows/workflow-control/app-logo.tsx
 * */
const NAME_MAP: Record<string, string> = {
  zapier_zap_trigger: "Zap",
  zapier_bot_dm_trigger: "On demand",
};

const ZAP_SVG_SRC =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 2 20 20"><path fill="%23FF4F00" d="M11 12H7.74996L13 5.75V12H16.25L11 18.25V16H8.99996V23.75L20.54 10H15V0.25L3.45996 14H11V12Z"/></svg>';

const ON_DEMAND_SVG_SRC =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 2 20 20"><path fill="%23503ebd" d="M5 20.37L19.5 12L5 3.63V20.37ZM7 7.09L15.5 12L7 16.91V7.09Z"/></svg>';

const SRC_MAP: Record<string, string> = {
  zapier_zap_trigger: ZAP_SVG_SRC,
  zapier_bot_dm_trigger: ON_DEMAND_SVG_SRC,
};

function SuspendedAgentThumbnail({ agentId }: Props) {
  const agentResult = useAtomValue(agentAtomFamily_SUSPENDS(agentId)) as
    | AgentFetchResult
    | null
    | undefined;
  const getService = useServiceById();
  const hasStreamingProgressStarted = useAtomValue(
    hasStreamingProgressStartedAtom,
  );

  // Determine view state (treat null as generic error/unavailable)
  const isSuccess = agentResult?.type === "success";

  /* ------------------------------------------------------------
   * Success ‑ hydrate workflow app icons & instruction preview
   * ---------------------------------------------------------- */
  const agent = isSuccess ? agentResult.data : undefined;
  const hasInstructions = isSuccess
    ? Boolean(
        agent?.workflow.instructions_preview_text ||
          agent?.workflow.instructions,
      )
    : false;

  const uniqueWorkflowAppsHydrated = isSuccess
    ? getUniqueAgentWorkflowApps(agent!.workflow).map((appId) => {
        const size = 20;
        const service = getService(appId);
        const images = service?.images;
        const name = NAME_MAP[appId] ?? service?.name ?? cleanUpAppId(appId);
        const src = SRC_MAP[appId] ?? getAppIconUrl(appId, images, size);

        return {
          name,
          src,
        };
      })
    : [];

  const serviceNames = uniqueWorkflowAppsHydrated
    .map((app) => app.name)
    .join(", ");

  /* ------------------------------------------------------------
   * Text content based on agent result
   * ---------------------------------------------------------- */
  // Default fallback copy for unknown / unavailable agent responses
  let nameText = "Agent Unavailable";
  let instructionsText = "Unable to load agent details";

  if (agentResult) {
    switch (agentResult.type) {
      case "success":
        nameText = agent!.name || "Untitled Agent";
        instructionsText = hasInstructions
          ? agent!.workflow.instructions_preview_text ||
            agent!.workflow.instructions
          : "This agent requires more instructions";
        break;
      case "not_found":
        nameText = "404 - Agent Not Found";
        instructionsText = "This agent could not be found";
        break;
      case "forbidden":
        nameText = "403 - Access Denied";
        instructionsText = "You don't have access to this agent";
        break;
    }
  }

  /* This way we don't see "Agent Unavailable" header with "Unable to load agent details" instructions */
  if (hasStreamingProgressStarted) {
    return <AgentThumbnailLoader />;
  }

  return (
    <div className={styles.root}>
      {isSuccess && Boolean(uniqueWorkflowAppsHydrated.length) && (
        <div className={styles.tooltipContainer}>
          <Tooltip placement="top" content={serviceNames}>
            <Tooltip.FocusableTrigger>
              <ServiceIcons
                hasBorder
                maxServices={3}
                size="small"
                services={uniqueWorkflowAppsHydrated}
              />
            </Tooltip.FocusableTrigger>
          </Tooltip>
        </div>
      )}
      <Text type="Body2Multiline" color="GrayWarm9">
        {nameText}
      </Text>
      <Text type="SmallPrint1" color="TextWeakest">
        <div className={styles.lineClamp3}>{instructionsText}</div>
      </Text>
    </div>
  );
}

function AgentThumbnailLoader() {
  return (
    <SkeletonText
      lines={3}
      size="body"
      textAlign="left"
      width="100%"
      color="GrayWarm3"
    />
  );
}

export function AgentThumbnail({ agentId }: Props) {
  return (
    <Suspense fallback={<AgentThumbnailLoader />}>
      <SuspendedAgentThumbnail agentId={agentId} />
    </Suspense>
  );
}
